/**
 * Order Booking Creatives — content writer panel configuration.
 *
 * Single source of truth for the content status model, sub-types, tabs and
 * endpoint paths described in the shared API contract. Nothing in this folder
 * should hard-code a status number or an endpoint string.
 *
 * This status model is its OWN namespace (1-5) and must never be confused
 * with the designer panel's 1-8 design-status model that lives in the same
 * shared `bsc` row and the same shared timeline table.
 */

export const STATUS = {
  1: { value: 1, key: 'assigned', label: 'Assigned', pill: 'bg-amber-50 text-amber-700 ring-amber-200' },
  2: { value: 2, key: 'picked', label: 'Picked', pill: 'bg-sky-50 text-sky-700 ring-sky-200' },
  3: { value: 3, key: 'submitted', label: 'Submitted', pill: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  4: { value: 4, key: 'rejected', label: 'Rejected', pill: 'bg-rose-50 text-rose-700 ring-rose-200' },
  5: { value: 5, key: 'approved', label: 'Approved', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
};

export const UNKNOWN_STATUS = {
  value: 0,
  key: 'unknown',
  label: 'Unknown',
  pill: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export const getStatus = (status) => STATUS[Number(status)] || UNKNOWN_STATUS;

export const SUB_TYPE_LABELS = {
  full_image: 'Full Image',
  full_video: 'Full Video',
  promoted_image_story: 'Promoted Image Story',
  promoted_video_story: 'Promoted Video Story',
};

export const VIDEO_SUB_TYPES = ['full_video', 'promoted_video_story'];

export const PROMOTED_SUB_TYPES = ['promoted_image_story', 'promoted_video_story'];

export const SUB_TYPE_OPTIONS = Object.keys(SUB_TYPE_LABELS).map((value) => ({
  value,
  label: SUB_TYPE_LABELS[value],
}));

export const TABS = [
  { key: 'assigned', label: 'Assigned', status: 1 },
  { key: 'picked', label: 'Picked', status: 2 },
  { key: 'submitted', label: 'Submitted', status: 3 },
  { key: 'rejected', label: 'Rejected', status: 4 },
  { key: 'approved', label: 'Approved', status: 5 },
];

export const TAB_KEYS = TABS.map((t) => t.key);

export const DEFAULT_TAB = 'assigned';

export const getTab = (key) => TABS.find((t) => t.key === key) || TABS[0];

/** Short, tab-specific empty-state copy (never a generic "No data"). */
export const TAB_EMPTY_COPY = {
  assigned: {
    title: 'Nothing waiting on you',
    line: 'New creatives assigned to you by sales will land here. Pick one to start writing.',
  },
  picked: {
    title: 'No drafts in progress',
    line: 'Pick a creative from the Assigned tab and it will move here while you write for it.',
  },
  submitted: {
    title: 'Nothing submitted yet',
    line: 'Once you submit your drafts they move here and wait for the sales review.',
  },
  rejected: {
    title: 'No rework requested',
    line: 'Creatives sent back with change requests will show up here.',
  },
  approved: {
    title: 'No approved content yet',
    line: 'Approved content is collected here once sales signs off on it.',
  },
};

export const ENDPOINTS = {
  counts: '/user/booking-content-writer/counts',
  list: '/user/booking-content-writer/list',
  detail: '/user/booking-content-writer/detail',
  timeline: '/user/booking-content-writer/timeline',
  pick: '/user/booking-content-writer/pick',
  message: '/user/booking-content-writer/message',
  uploadAttachment: '/user/booking-content-writer/upload-attachment',
  submitVariations: '/user/booking-content-writer/submit-variations',
};

export const PAGE_SIZE = 12;

/** localStorage key holding `{ tab, filters }`. */
export const STORAGE_KEY = 'ob_content_writer_filters';

/* ------------------------------------------------------------------ */
/* Permissions — mirrors the status/action matrix in the shared spec.  */
/* ------------------------------------------------------------------ */

export const canPick = (status) => Number(status) === 1;

export const canSubmitVariations = (status) => [2, 3, 4].includes(Number(status));

// The backend applies NO status gate to /message or /upload-attachment — a
// content writer can talk about a creative at any status, including after
// it's approved. Do not invent a gate that isn't in the API contract.
export const canMessage = () => true;
export const canAttach = () => true;

export const isVideoSubType = (subType) => VIDEO_SUB_TYPES.includes(String(subType || ''));

export const isPromotedSubType = (subType) => PROMOTED_SUB_TYPES.includes(String(subType || ''));

export const subTypeLabel = (subType) => SUB_TYPE_LABELS[subType] || subType || '—';

/**
 * The three fields a draft variation can carry. Every sub-type may use any
 * of them — the backend only requires at least one non-empty field per
 * variation, so the form does not gate fields by sub-type.
 */
export const VARIATION_FIELDS = [
  { key: 'head_line', label: 'Headline', kind: 'text', placeholder: 'Write a headline…' },
  { key: 'description', label: 'Description', kind: 'textarea', placeholder: 'Write a description…' },
  { key: 'text_in_link', label: 'Link text', kind: 'text', placeholder: 'Write the link text…' },
];

export const emptyVariation = () => ({ head_line: '', description: '', text_in_link: '' });

/** True when at least one of the three fields carries real text. */
export const isVariationFilled = (variation) =>
  VARIATION_FIELDS.some((f) => String(variation?.[f.key] || '').trim());

/* ------------------------------------------------------------------ */
/* Formatting helpers — every timestamp on the wire is epoch seconds.  */
/* ------------------------------------------------------------------ */

const toDate = (sec) => {
  const n = Number(sec);
  if (!n || Number.isNaN(n) || n <= 0) return null;
  return new Date(n * 1000);
};

export const formatEpoch = (sec) => {
  const d = toDate(sec);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const formatEpochDate = (sec) => {
  const d = toDate(sec);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

export const formatEpochTime = (sec) => {
  const d = toDate(sec);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

/** "just now" / "4m" / "3h" / "6d" / falls back to a date past 30 days. */
export const formatRelative = (sec) => {
  const d = toDate(sec);
  if (!d) return '—';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 0) return 'just now';
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return formatEpochDate(sec);
};

const IMAGE_RE = /\.(jpe?g|png|gif|webp|avif|svg)(\?.*)?$/i;
const VIDEO_RE = /\.(mp4|webm|ogg|mov|avi|m4v)(\?.*)?$/i;

export const looksLikeImage = (url) => IMAGE_RE.test(String(url || ''));
export const looksLikeVideo = (url) => VIDEO_RE.test(String(url || ''));

/** Normalised, stable shape for the filter bar — also the localStorage shape. */
export const EMPTY_FILTERS = {
  query: '',
  sub_type: '',
  start_date: '',
  end_date: '',
  sort_by_latest_activity: false,
};

// NOTE: `query` is deliberately NOT trimmed here — trimming a committed value
// would yank a trailing space back out of the search box while the user types.
// The trim happens once, at the API boundary.
export const normaliseFilters = (filters) => ({
  query: String(filters?.query || ''),
  sub_type: filters?.sub_type || '',
  start_date: filters?.start_date || '',
  end_date: filters?.end_date || '',
  sort_by_latest_activity: !!filters?.sort_by_latest_activity,
});

/**
 * Primitive, order-stable key for a filter object. Effects depend on THIS,
 * never on the object itself — object literals in deps re-run every render.
 */
export const filterKeyOf = (filters) => JSON.stringify(normaliseFilters(filters));

export const hasActiveFilters = (filters) => {
  const f = normaliseFilters(filters);
  return !!(
    f.query.trim() ||
    f.sub_type ||
    f.start_date ||
    f.end_date ||
    f.sort_by_latest_activity
  );
};
